import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, of } from 'rxjs';
import { environment } from '../../../../environments/environment';

interface BrandListItem {
  name: string;
  domain: string;
}

/**
 * Bridges the unified-search result card — which only carries the brand NAME
 * from the ES index — to `cmt-brand-logo`, which needs the brand's CDN asset
 * DOMAIN. Loads `GET /contract/v2/rest/brands` once and builds a
 * case-insensitive name→domain lookup.
 *
 * This call is independent of `SessionService`'s own brand load (id→name,
 * scoped to the active customer's brand chip): this service keys by NAME —
 * what the search index returns — and exists purely to resolve logos for
 * search results, whether or not a customer session is active.
 */
@Injectable({ providedIn: 'root' })
export class BrandDirectoryService {
  private readonly http = inject(HttpClient);
  private loaded = false;

  private readonly nameToDomain = signal<Map<string, string>>(new Map());

  /** Fire-and-forget: safe to call repeatedly, only fetches once (per successful load). */
  ensureLoaded(): void {
    if (this.loaded) return;
    this.loaded = true;
    this.http.get<BrandListItem[]>(`${environment.contractUrl}/brands`).pipe(
      catchError(() => {
        // Reset the guard so a later call retries instead of being stuck logo-less until a
        // full page reload; degrades gracefully to name-only in the meantime (AD-16).
        this.loaded = false;
        return of([] as BrandListItem[]);
      }),
    ).subscribe((brands) => {
      const map = new Map<string, string>();
      for (const brand of brands) {
        if (brand.name && brand.domain) map.set(brand.name.toLowerCase(), brand.domain);
      }
      this.nameToDomain.set(map);
    });
  }

  /** Case-insensitive brand-name → CDN asset domain lookup. Reads the signal so callers
   * inside a `computed()` react once the brand list finishes loading. */
  domainForName(name: string | null | undefined): string | null {
    if (!name) return null;
    return this.nameToDomain().get(name.toLowerCase()) ?? null;
  }
}
